/**
 * 🎬 Demo Extra SceneFrames — 6 nouveaux mockups premium pour la démo cinematic
 * =============================================================================
 *
 * Étend les 6 scènes existantes de HeroDemoVideo (overview, owen, certifiant,
 * humain, rapport, prism) avec :
 *   - SceneProblem (Problème managérial)
 *   - SceneTheory (Théorie pédagogique)
 *   - SceneStructure (2 objectifs · 5 critères)
 *   - SceneSequential (logique séquentielle IA)
 *   - ScenePulse (dashboard équipe)
 *   - ScenePulseRoom (salle de présentation RH/direction)
 *   - SceneTransformation (transformation finale + CTA)
 *
 * Design system identique à HeroDemoVideo (palette T, animations Framer Motion).
 */

import React from 'react';
import { motion } from 'framer-motion';
import {
  AlertTriangle,
  BookOpen,
  Layers,
  CheckCircle2,
  Lock,
  Unlock,
  Activity,
  TrendingUp,
  Eye,
  Award,
  Sparkles,
  ArrowRight,
  Presentation,
  Target,
  Brain,
  Zap,
} from 'lucide-react';
import { T } from '../HeroDemoVideo';
import DemoShareCluster from './DemoShareCluster';

/* ============================================================
   01 · PROBLÈME MANAGÉRIAL — Scène d'ouverture (refonte premium)
   ============================================================ */

// Hook count-up : valeur 0 → target en `duration`ms (cubic ease-out)
function useCountUp(target, duration = 1500, delay = 0) {
  const [val, setVal] = React.useState(0);
  React.useEffect(() => {
    let raf = null;
    const t0 = performance.now() + delay;
    const tick = (now) => {
      const t = now - t0;
      if (t < 0) {
        raf = requestAnimationFrame(tick);
        return;
      }
      const ratio = Math.min(1, t / duration);
      const eased = 1 - Math.pow(1 - ratio, 3); // ease-out cubic
      setVal(Math.round(target * eased));
      if (ratio < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => raf && cancelAnimationFrame(raf);
  }, [target, duration, delay]);
  return val;
}

// Sparkline animée (path qui se trace en draw progressif)
const ProblemSparkline = ({ delay = 1.0 }) => {
  // Ligne descendante symbolisant la perte de progression sans feedback
  const points = '0,28 12,24 24,26 36,22 48,25 60,18 72,20 84,12 96,14 108,8';
  return (
    <svg width="120" height="32" viewBox="0 0 120 32" className="overflow-visible">
      <defs>
        <linearGradient id="sparkGradient" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#C44B6F" stopOpacity="0.2" />
          <stop offset="100%" stopColor="#C44B6F" stopOpacity="0.9" />
        </linearGradient>
      </defs>
      <motion.polyline
        points={points}
        fill="none"
        stroke="url(#sparkGradient)"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
        initial={{ pathLength: 0, opacity: 0 }}
        animate={{ pathLength: 1, opacity: 1 }}
        transition={{ duration: 1.6, delay, ease: 'easeOut' }}
      />
      <motion.circle
        cx="108"
        cy="8"
        r="2.5"
        fill="#C44B6F"
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: delay + 1.5, type: 'spring', stiffness: 380, damping: 14 }}
      />
    </svg>
  );
};

export function SceneProblem() {
  const pct = useCountUp(78, 1500, 350);
  return (
    <div className="absolute inset-0 p-5 sm:p-7 flex flex-col">
      {/* HEADER : badge 2026 + ligne fine animée + halo */}
      <div className="relative flex items-center gap-3 mb-3">
        <motion.span
          initial={{ opacity: 0, y: -6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[9px] font-bold uppercase tracking-[0.20em]"
          style={{
            background: 'linear-gradient(135deg, rgba(196,75,111,0.14), rgba(196,75,111,0.04))',
            border: '1px solid rgba(196,75,111,0.36)',
            color: '#C44B6F',
          }}
        >
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: '#C44B6F', boxShadow: '0 0 6px #C44B6F' }} />
          Constat · 2026
        </motion.span>
        <motion.div
          aria-hidden
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ duration: 1.2, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
          style={{
            flex: 1,
            height: 1,
            background: 'linear-gradient(90deg, rgba(196,75,111,0.40) 0%, rgba(196,75,111,0) 100%)',
            transformOrigin: 'left',
          }}
        />
      </div>

      {/* TITRE + accroche */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.25 }}
        className="font-serif"
        style={{ color: T.ink, fontSize: '1.25rem', fontWeight: 700, lineHeight: 1.12 }}
      >
        La plupart des managers ne progressent pas.
      </motion.div>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.45 }}
        className="text-[12px] leading-snug mt-1.5"
        style={{ color: T.inkSoft }}
      >
        Parce qu'ils ne sont jamais évalués sur leurs décisions.
      </motion.div>

      {/* KPIs animés */}
      <div className="grid grid-cols-3 gap-2 mt-4">
        {/* 78% : count-up animé */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.55 }}
          className="rounded-xl p-2.5 text-center relative overflow-hidden"
          style={{
            background: '#FFFFFF',
            border: `1px solid ${T.border}`,
            boxShadow: '0 4px 12px -6px rgba(196,75,111,0.22)',
          }}
        >
          <div
            className="font-serif text-2xl font-bold tabular-nums"
            style={{ color: '#C44B6F' }}
          >
            {pct}%
          </div>
          <div className="text-[9px] mt-0.5" style={{ color: T.inkSoft, lineHeight: 1.15 }}>
            sans feedback structuré
          </div>
        </motion.div>

        {/* 0 : apparition brutale spring */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="rounded-xl p-2.5 text-center relative overflow-hidden"
          style={{
            background: '#FFFFFF',
            border: `1px solid ${T.border}`,
            boxShadow: '0 4px 12px -6px rgba(196,75,111,0.22)',
          }}
        >
          <motion.div
            initial={{ scale: 0.4, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 1.2, type: 'spring', stiffness: 320, damping: 12 }}
            className="font-serif text-2xl font-bold"
            style={{ color: '#C44B6F' }}
          >
            0
          </motion.div>
          <div className="text-[9px] mt-0.5" style={{ color: T.inkSoft, lineHeight: 1.15 }}>
            preuve mesurable
          </div>
        </motion.div>

        {/* ∅ : fade-in + shake léger */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.85 }}
          className="rounded-xl p-2.5 text-center relative overflow-hidden"
          style={{
            background: '#FFFFFF',
            border: `1px solid ${T.border}`,
            boxShadow: '0 4px 12px -6px rgba(196,75,111,0.22)',
          }}
        >
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1, x: [0, -2, 2, -1, 1, 0] }}
            transition={{ delay: 1.6, duration: 0.5 }}
            className="font-serif text-2xl font-bold"
            style={{ color: '#C44B6F' }}
          >
            ∅
          </motion.div>
          <div className="text-[9px] mt-0.5" style={{ color: T.inkSoft, lineHeight: 1.15 }}>
            progression visible
          </div>
        </motion.div>
      </div>

      {/* Pill alerte */}
      <motion.div
        initial={{ opacity: 0, x: -8 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 1.3 }}
        className="mt-3 inline-flex items-center gap-2 self-start px-3 py-1.5 rounded-full"
        style={{
          background: 'rgba(196,75,111,0.10)',
          border: '1px solid rgba(196,75,111,0.30)',
        }}
      >
        <AlertTriangle className="w-3.5 h-3.5" style={{ color: '#C44B6F' }} />
        <span className="text-[10px] font-semibold" style={{ color: '#C44B6F' }}>
          Former ne suffit plus.
        </span>
      </motion.div>

      {/* FOOTER : barre de progression "à plat" + sparkline descendante */}
      <div className="mt-auto pt-3 flex items-end justify-between gap-3">
        <div className="flex-1">
          <div className="text-[8.5px] uppercase tracking-[0.16em] font-semibold mb-1" style={{ color: T.inkSoft }}>
            Progression managériale réelle
          </div>
          <div
            className="h-1.5 rounded-full overflow-hidden"
            style={{ background: 'rgba(28,17,71,0.08)' }}
          >
            <motion.div
              initial={{ width: '0%' }}
              animate={{ width: '8%' }}
              transition={{ duration: 1.4, delay: 1.4, ease: 'easeOut' }}
              className="h-full rounded-full"
              style={{
                background: 'linear-gradient(90deg, #C44B6F, #E27693)',
                boxShadow: '0 0 8px rgba(196,75,111,0.6)',
              }}
            />
          </div>
        </div>
        <ProblemSparkline delay={1.0} />
      </div>
    </div>
  );
}

/* ============================================================
   02 · PARTIE THÉORIQUE — Cadre conceptuel + modules animés
   ============================================================ */
export function SceneTheory() {
  const concepts = [
    'Posture managériale',
    'Cadre & critères',
    'Décision factuelle',
    'Boucle de feedback',
  ];
  const modules = [
    { num: '02', name: 'Cadrer la décision', pct: 88, color: T.goldDeep },
    { num: '05', name: 'Décider sous incertitude', pct: 64, color: T.violet },
    { num: '07', name: 'Vérifier les preuves', pct: 41, color: T.green },
  ];
  return (
    <div className="absolute inset-0 p-5 sm:p-7 flex flex-col gap-2.5">
      <span
        className="text-[9px] uppercase tracking-[0.20em] font-semibold"
        style={{ color: T.goldDeep }}
      >
        Théorie · Module 02
      </span>
      <div
        className="font-serif"
        style={{ color: T.ink, fontSize: '1.05rem', fontWeight: 600, lineHeight: 1.2 }}
      >
        Cadrer · Décider · Vérifier
      </div>
      <div
        className="rounded-xl p-2.5"
        style={{
          background: 'linear-gradient(180deg, #FFFCEC 0%, #FFFFFF 100%)',
          border: `1px solid ${T.border}`,
        }}
      >
        <div className="flex items-center gap-2 mb-2">
          <BookOpen className="w-3.5 h-3.5" style={{ color: T.goldDeep }} />
          <span
            className="text-[9px] uppercase tracking-[0.18em] font-semibold"
            style={{ color: T.goldDeep }}
          >
            Lecture pédagogique · 30 min
          </span>
        </div>
        <div className="grid grid-cols-2 gap-1.5">
          {concepts.map((c, i) => (
            <motion.div
              key={c}
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.15 + i * 0.1 }}
              className="rounded-lg px-2 py-1.5 flex items-center gap-1.5"
              style={{ background: '#FFFFFF', border: `1px solid ${T.border}` }}
            >
              <span
                className="w-1.5 h-1.5 rounded-full"
                style={{ background: T.goldDeep }}
              />
              <span className="text-[10px] font-medium" style={{ color: T.ink }}>
                {c}
              </span>
            </motion.div>
          ))}
        </div>
      </div>

      {/* 📚 Liste 3 modules — surlignage du module en lecture (pulse violet) */}
      <div className="space-y-1">
        <div className="text-[8.5px] uppercase tracking-[0.16em] font-semibold mb-0.5" style={{ color: T.inkSoft }}>
          Modules en cours d'étude
        </div>
        {modules.map((m, i) => {
          const isActive = i === 0; // module 02 = celui en cours
          return (
            <motion.div
              key={m.num}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.6 + i * 0.12, duration: 0.4 }}
              className="flex items-center gap-2.5 px-2 py-1.5 rounded-md"
              style={{
                background: isActive ? 'rgba(122,79,207,0.08)' : 'rgba(28,17,71,0.025)',
                border: isActive ? '1px solid rgba(122,79,207,0.30)' : '1px solid transparent',
                boxShadow: isActive ? '0 0 0 3px rgba(122,79,207,0.10)' : 'none',
              }}
            >
              <span
                className="text-[9px] font-bold tabular-nums"
                style={{ color: m.color, minWidth: 14 }}
              >
                {m.num}
              </span>
              {isActive && (
                <motion.span
                  className="w-1.5 h-1.5 rounded-full"
                  style={{ background: T.violet, boxShadow: `0 0 8px ${T.violet}` }}
                  animate={{ opacity: [0.4, 1, 0.4] }}
                  transition={{ duration: 1.2, repeat: Infinity }}
                />
              )}
              <span className="text-[10.5px] font-medium flex-1 truncate" style={{ color: T.ink }}>
                {m.name}
              </span>
              <div className="w-12 h-0.5 rounded-full overflow-hidden flex-shrink-0" style={{ background: 'rgba(28,17,71,0.07)' }}>
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${m.pct}%` }}
                  transition={{ duration: 0.7, delay: 0.7 + i * 0.12 }}
                  className="h-full"
                  style={{ background: `linear-gradient(90deg, ${m.color}, ${T.goldLight})` }}
                />
              </div>
              <span
                className="text-[8.5px] font-semibold tabular-nums"
                style={{ color: m.color, minWidth: 26, textAlign: 'right' }}
              >
                {m.pct}%
              </span>
            </motion.div>
          );
        })}
      </div>

      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.1 }}
        className="mt-auto rounded-lg p-2.5"
        style={{
          background: 'rgba(212,175,55,0.10)',
          border: '1px solid rgba(212,175,55,0.30)',
        }}
      >
        <div
          className="text-[10px] font-semibold"
          style={{ color: T.goldDeep }}
        >
          Concept central
        </div>
        <div className="text-[11px] leading-snug mt-0.5" style={{ color: T.ink }}>
          Une décision n'est valable que si elle est cadrée, factualisée et suivie.
        </div>
      </motion.div>
    </div>
  );
}

/* ============================================================
   03 · STRUCTURE 2 OBJECTIFS · 5 CRITÈRES — explicitement détaillés
   ============================================================ */
export function SceneStructure() {
  const objectives = [
    {
      id: 'A',
      label: 'Objectif A · Cadrer la décision',
      status: 'in_progress',
      criteria: [
        { id: 'C1', label: 'Cadre énoncé clairement', state: 'done' },
        { id: 'C2', label: 'Mesure de succès définie', state: 'done' },
        { id: 'C3', label: 'Validation par Owen', state: 'active' },
        { id: 'C4', label: 'Preuve écrite documentée', state: 'todo' },
        { id: 'C5', label: 'Suivi engagé sur 30 jours', state: 'todo' },
      ],
    },
    {
      id: 'B',
      label: 'Objectif B · Vérifier les preuves',
      status: 'locked',
      criteria: [
        { id: 'C1', label: 'Source factuelle citée', state: 'todo' },
        { id: 'C2', label: 'Triangulation données', state: 'todo' },
        { id: 'C3', label: 'Validation par Owen', state: 'todo' },
        { id: 'C4', label: 'Preuve écrite documentée', state: 'todo' },
        { id: 'C5', label: 'Décision tracée et signée', state: 'todo' },
      ],
    },
  ];

  return (
    <div className="absolute inset-0 p-4 sm:p-5 flex flex-col gap-2">
      <span
        className="text-[9px] uppercase tracking-[0.20em] font-semibold"
        style={{ color: T.violet }}
      >
        Structure pédagogique
      </span>
      <div
        className="font-serif"
        style={{ color: T.ink, fontSize: '0.95rem', fontWeight: 600, lineHeight: 1.18 }}
      >
        2 objectifs · 5 critères chacun
      </div>

      {objectives.map((obj, oi) => {
        const isLocked = obj.status === 'locked';
        const validatedCount = obj.criteria.filter((c) => c.state === 'done').length;
        return (
          <motion.div
            key={obj.id}
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.15 + oi * 0.18 }}
            className="rounded-xl p-2"
            style={{
              background: '#FFFFFF',
              border: `1px solid ${T.border}`,
              opacity: isLocked ? 0.7 : 1,
              boxShadow: isLocked ? 'none' : '0 4px 12px -8px rgba(122,79,207,0.32)',
            }}
          >
            {/* Header objectif */}
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-1.5">
                {isLocked ? (
                  <Lock className="w-3 h-3" style={{ color: T.inkSoft }} />
                ) : (
                  <Unlock className="w-3 h-3" style={{ color: T.violet }} />
                )}
                <span
                  className="text-[10.5px] font-semibold"
                  style={{ color: T.ink }}
                >
                  {obj.label}
                </span>
              </div>
              <span
                className="text-[9px] font-bold tabular-nums"
                style={{ color: isLocked ? T.inkSoft : T.violet }}
              >
                {validatedCount}/5
              </span>
            </div>

            {/* Mini-progress bar */}
            <div
              className="h-0.5 rounded-full overflow-hidden mb-1.5"
              style={{ background: 'rgba(28,17,71,0.08)' }}
            >
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${(validatedCount / 5) * 100}%` }}
                transition={{ duration: 0.7, delay: 0.4 + oi * 0.18 }}
                className="h-full"
                style={{
                  background: `linear-gradient(90deg, ${T.violet}, ${T.violetSoft})`,
                  boxShadow: isLocked ? 'none' : `0 0 8px ${T.violetSoft}66`,
                }}
              />
            </div>

            {/* 5 critères listés explicitement (cascade) */}
            <div className="grid grid-cols-1 gap-0.5">
              {obj.criteria.map((c, ci) => (
                <motion.div
                  key={c.id}
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.5 + oi * 0.18 + ci * 0.07 }}
                  className="flex items-center gap-1.5 px-1 py-0.5 rounded"
                  style={{
                    background:
                      c.state === 'active' ? 'rgba(212,175,55,0.10)' : 'transparent',
                    border:
                      c.state === 'active'
                        ? '1px solid rgba(212,175,55,0.36)'
                        : '1px solid transparent',
                  }}
                >
                  {c.state === 'done' ? (
                    <CheckCircle2
                      className="w-3 h-3 flex-shrink-0"
                      style={{ color: T.green }}
                    />
                  ) : c.state === 'active' ? (
                    <motion.span
                      className="w-3 h-3 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{
                        background: T.gold,
                        boxShadow: `0 0 8px ${T.gold}`,
                      }}
                      animate={{ scale: [1, 1.18, 1] }}
                      transition={{ duration: 1.1, repeat: Infinity }}
                    >
                      <span className="w-0.5 h-0.5 rounded-full bg-white" />
                    </motion.span>
                  ) : (
                    <span
                      className="w-3 h-3 rounded-full border-[1.5px] flex-shrink-0"
                      style={{ borderColor: 'rgba(28,17,71,0.25)' }}
                    />
                  )}
                  <span
                    className="text-[9.5px] font-bold tabular-nums w-4 flex-shrink-0"
                    style={{
                      color:
                        c.state === 'done'
                          ? T.green
                          : c.state === 'active'
                          ? T.goldDeep
                          : T.inkSoft,
                    }}
                  >
                    {c.id}
                  </span>
                  <span
                    className="text-[9.5px] font-medium flex-1 truncate"
                    style={{
                      color: c.state === 'todo' ? T.inkSoft : T.ink,
                      textDecoration:
                        c.state === 'done' ? 'none' : 'none',
                    }}
                  >
                    {c.label}
                  </span>
                  {c.state === 'active' && (
                    <span
                      className="text-[7.5px] font-bold uppercase tracking-[0.14em]"
                      style={{ color: T.goldDeep }}
                    >
                      en cours
                    </span>
                  )}
                  {c.state === 'done' && (
                    <span
                      className="text-[7.5px] font-bold uppercase tracking-[0.14em]"
                      style={{ color: T.green }}
                    >
                      validé
                    </span>
                  )}
                </motion.div>
              ))}
            </div>
          </motion.div>
        );
      })}

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4 }}
        className="mt-auto inline-flex items-center gap-1.5 self-start text-[9.5px] font-medium px-2 py-1 rounded-full"
        style={{
          background: 'rgba(28,17,71,0.04)',
          color: T.inkSoft,
          border: `1px solid ${T.border}`,
        }}
      >
        <Layers className="w-3 h-3" />
        Aucun critère validé sans preuve concrète observée.
      </motion.div>
    </div>
  );
}

/* ============================================================
   04 · PROCESSUS SÉQUENTIEL IA — théorie → certifiant → preuve
   ============================================================ */
export function SceneSequential() {
  const steps = [
    { label: 'Théorie', icon: BookOpen, status: 'done', accent: T.goldDeep },
    { label: 'Owen IA', icon: Brain, status: 'done', accent: T.violet },
    { label: 'Certifiant', icon: Award, status: 'active', accent: T.gold },
    { label: 'Humain', icon: Activity, status: 'locked', accent: T.green },
    { label: 'Rapport', icon: CheckCircle2, status: 'locked', accent: T.violet },
  ];
  return (
    <div className="absolute inset-0 p-5 sm:p-7 flex flex-col gap-3">
      <span
        className="text-[9px] uppercase tracking-[0.20em] font-semibold"
        style={{ color: T.violet }}
      >
        Processus séquentiel IA
      </span>
      <div
        className="font-serif"
        style={{ color: T.ink, fontSize: '1rem', fontWeight: 600, lineHeight: 1.2 }}
      >
        Une étape ne s'ouvre qu'après preuve de la précédente.
      </div>
      <div className="flex items-center gap-1.5 mt-2">
        {steps.map((s, i) => {
          const Icon = s.icon;
          const isLast = i === steps.length - 1;
          return (
            <React.Fragment key={s.label}>
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.15 + i * 0.12, type: 'spring', stiffness: 220 }}
                className="flex flex-col items-center gap-1 flex-1"
              >
                <div
                  className="w-9 h-9 rounded-xl flex items-center justify-center"
                  style={{
                    background:
                      s.status === 'done'
                        ? s.accent
                        : s.status === 'active'
                        ? `linear-gradient(135deg, ${s.accent}, ${T.goldLight})`
                        : '#FFFFFF',
                    border: s.status === 'locked' ? `1px solid ${T.border}` : 'none',
                    boxShadow:
                      s.status === 'active'
                        ? `0 0 0 4px ${T.gold}33, 0 6px 14px -6px ${s.accent}88`
                        : '0 4px 10px -6px rgba(28,17,71,0.18)',
                  }}
                >
                  <Icon
                    className="w-4 h-4"
                    style={{
                      color: s.status === 'locked' ? T.inkSoft : '#FFFFFF',
                    }}
                  />
                </div>
                <span
                  className="text-[9px] font-semibold"
                  style={{
                    color:
                      s.status === 'locked'
                        ? T.inkSoft
                        : s.status === 'active'
                        ? T.goldDeep
                        : T.ink,
                  }}
                >
                  {s.label}
                </span>
                {s.status === 'active' && (
                  <span
                    className="text-[8px] font-bold uppercase tracking-[0.16em]"
                    style={{ color: T.goldDeep }}
                  >
                    En cours
                  </span>
                )}
              </motion.div>
              {!isLast && (
                <motion.span
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  transition={{ delay: 0.25 + i * 0.12, duration: 0.4 }}
                  className="block h-0.5 flex-1 rounded-full origin-left"
                  style={{
                    background:
                      i < 1
                        ? T.goldDeep
                        : i < 2
                        ? `linear-gradient(90deg, ${T.violet}, ${T.gold})`
                        : 'rgba(28,17,71,0.10)',
                    marginTop: -16,
                  }}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8 }}
        className="rounded-lg p-2.5"
        style={{
          background: 'linear-gradient(135deg, #F4EFFF 0%, #FFFCEC 100%)',
          border: `1px solid rgba(122,79,207,0.30)`,
        }}
      >
        <div className="flex items-center gap-1.5 mb-1">
          <Zap className="w-3 h-3" style={{ color: T.violet }} />
          <span
            className="text-[9px] font-semibold uppercase tracking-[0.16em]"
            style={{ color: T.violet }}
          >
            Logique de preuve
          </span>
        </div>
        <div className="text-[10.5px] leading-snug" style={{ color: T.ink }}>
          Aucune étape n'est franchie sans preuve mesurable validée par Owen.
          Le certificat n'est délivré que lorsque le module est entièrement validé.
        </div>
      </motion.div>

      {/* 5 critères animés — étape Certifiant en cours */}
      <div className="mt-2 space-y-1">
        <div className="flex items-center justify-between mb-0.5">
          <span className="text-[8.5px] uppercase tracking-[0.16em] font-semibold" style={{ color: T.inkSoft }}>
            Critères · Étape Certifiant
          </span>
          <span className="text-[8.5px] font-bold tabular-nums" style={{ color: T.goldDeep }}>
            3/5 validés
          </span>
        </div>
        {[
          { label: 'C1 · Cadre énoncé', state: 'done' },
          { label: 'C2 · Mesure définie', state: 'done' },
          { label: 'C3 · Validation Owen', state: 'active' },
          { label: 'C4 · Preuve écrite', state: 'todo' },
          { label: 'C5 · Suivi documenté', state: 'todo' },
        ].map((c, i) => (
          <motion.div
            key={c.label}
            initial={{ opacity: 0, x: -8 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.95 + i * 0.12 }}
            className="flex items-center gap-2 px-2 py-1 rounded-md"
            style={{
              background: c.state === 'done' ? 'rgba(72,130,90,0.06)' : c.state === 'active' ? 'rgba(212,175,55,0.10)' : 'transparent',
              border: c.state === 'active' ? '1px solid rgba(212,175,55,0.40)' : '1px solid transparent',
            }}
          >
            {c.state === 'done' ? (
              <CheckCircle2 className="w-3.5 h-3.5" style={{ color: T.green }} />
            ) : c.state === 'active' ? (
              <motion.span
                className="w-3.5 h-3.5 rounded-full flex items-center justify-center"
                style={{ background: T.gold, boxShadow: `0 0 8px ${T.gold}` }}
                animate={{ scale: [1, 1.15, 1] }}
                transition={{ duration: 1.1, repeat: Infinity }}
              >
                <span className="w-1 h-1 rounded-full bg-white" />
              </motion.span>
            ) : (
              <span className="w-3.5 h-3.5 rounded-full border-2" style={{ borderColor: 'rgba(28,17,71,0.18)' }} />
            )}
            <span
              className="text-[10px] font-medium flex-1"
              style={{ color: c.state === 'todo' ? T.inkSoft : T.ink }}
            >
              {c.label}
            </span>
            {c.state === 'active' && (
              <span
                className="text-[8.5px] font-bold uppercase tracking-[0.14em]"
                style={{ color: T.goldDeep }}
              >
                en cours
              </span>
            )}
          </motion.div>
        ))}
      </div>
    </div>
  );
}

/* ============================================================
   05 · PULSE — dashboard équipe leadership (anonymisé) — vue direction
   ============================================================ */
function ScoreCounter({ target = 82 }) {
  const [val, setVal] = React.useState(0);
  React.useEffect(() => {
    let raf;
    const start = performance.now();
    const dur = 1100;
    const tick = (now) => {
      const k = Math.min(1, (now - start) / dur);
      setVal(Math.round(target * (1 - Math.pow(1 - k, 3))));
      if (k < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target]);
  return <span className="font-serif tabular-nums">{val}</span>;
}

export function ScenePulse() {
  // 6 managers × 5 compétences (heatmap)
  const competences = ['Clarté', 'Décision', 'Écoute', 'Posture', 'Vision'];
  const team = [
    { name: 'Manager A', scores: [88, 92, 75, 80, 85], avg: 84 },
    { name: 'Manager B', scores: [72, 70, 88, 78, 75], avg: 77 },
    { name: 'Manager C', scores: [65, 58, 60, 70, 62], avg: 63 },
    { name: 'Manager D', scores: [90, 85, 92, 88, 86], avg: 88 },
    { name: 'Manager E', scores: [78, 82, 80, 75, 78], avg: 79 },
    { name: 'Manager F', scores: [82, 88, 76, 84, 90], avg: 84 },
  ];
  const colorFor = (v) => {
    if (v >= 85) return T.green;
    if (v >= 70) return T.violet;
    return '#E0A641';
  };

  // Auto-extract Points forts (≥85) et Points d'attention (<65)
  const strengths = [];
  const attentions = [];
  team.forEach((m, mi) => {
    m.scores.forEach((s, ci) => {
      if (s >= 90) strengths.push({ manager: m.name.replace('Manager ', ''), comp: competences[ci], score: s });
      if (s < 65) attentions.push({ manager: m.name.replace('Manager ', ''), comp: competences[ci], score: s });
    });
  });
  // Limiter à 2 par bloc pour densité optimale
  const topStrengths = strengths.sort((a, b) => b.score - a.score).slice(0, 2);
  const topAttentions = attentions.sort((a, b) => a.score - b.score).slice(0, 2);

  return (
    <div className="absolute inset-0 p-4 sm:p-5 flex flex-col gap-2">
      {/* Header avec score équipe animé */}
      <div className="flex items-center justify-between">
        <span className="text-[9px] uppercase tracking-[0.20em] font-semibold" style={{ color: T.violet }}>
          Pulse · Cartographie équipe
        </span>
        <div className="flex items-center gap-2">
          <span
            className="text-[9px] font-medium px-1.5 py-0.5 rounded-full"
            style={{
              background: 'rgba(72,130,90,0.12)',
              color: T.green,
              border: '1px solid rgba(72,130,90,0.30)',
            }}
          >
            Anonymisé
          </span>
          <div
            className="text-[10px] font-semibold px-2 py-0.5 rounded-full"
            style={{ background: T.ink, color: T.goldLight }}
          >
            Score équipe · <ScoreCounter target={82} />
          </div>
        </div>
      </div>
      <div className="font-serif" style={{ color: T.ink, fontSize: '0.92rem', fontWeight: 600, lineHeight: 1.15 }}>
        6 managers · 5 compétences · lecture direction
      </div>

      {/* Heatmap (6 managers en colonnes × 5 compétences en lignes) */}
      <div
        className="rounded-lg p-2"
        style={{ background: '#FFFFFF', border: `1px solid ${T.border}` }}
      >
        <div className="grid grid-cols-[60px_repeat(6,1fr)] gap-x-1 gap-y-0.5 items-center">
          <span></span>
          {team.map((m) => (
            <span key={m.name} className="text-[8px] font-semibold text-center" style={{ color: T.inkSoft }}>
              {m.name.replace('Manager ', '')}
            </span>
          ))}
          {competences.map((c, ci) => (
            <React.Fragment key={c}>
              <span className="text-[8.5px] font-semibold truncate" style={{ color: T.ink }}>
                {c}
              </span>
              {team.map((m, mi) => {
                const v = m.scores[ci];
                const isStrong = v >= 90;
                const isWeak = v < 65;
                return (
                  <motion.span
                    key={mi}
                    initial={{ opacity: 0, scale: 0.4 }}
                    animate={
                      isWeak
                        ? { opacity: 1, scale: [1, 1.08, 1] }
                        : { opacity: 1, scale: 1 }
                    }
                    transition={
                      isWeak
                        ? {
                            opacity: { delay: 0.2 + (ci * team.length + mi) * 0.04 },
                            scale: { duration: 1.4, repeat: Infinity, delay: 1.2 + mi * 0.15 },
                          }
                        : { delay: 0.2 + (ci * team.length + mi) * 0.04 }
                    }
                    className="rounded"
                    style={{
                      height: 14,
                      background: colorFor(v),
                      opacity: 0.4 + (v / 100) * 0.6,
                      boxShadow: isStrong
                        ? `0 0 10px ${T.green}, 0 0 0 1px ${T.green}aa`
                        : isWeak
                        ? `0 0 8px #E0A641aa`
                        : `0 0 4px ${colorFor(v)}55`,
                    }}
                    title={`${m.name} · ${c} : ${v}`}
                  />
                );
              })}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Points forts auto + Points d'attention auto */}
      <div className="grid grid-cols-2 gap-1.5">
        <motion.div
          initial={{ opacity: 0, x: -8 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 1.4 }}
          className="rounded-lg p-1.5"
          style={{
            background: '#FFFFFF',
            border: `1px solid ${T.green}33`,
            boxShadow: `0 4px 10px -8px ${T.green}66`,
          }}
        >
          <div
            className="text-[8px] uppercase tracking-[0.16em] font-bold mb-1 flex items-center gap-1"
            style={{ color: T.green }}
          >
            <CheckCircle2 className="w-2.5 h-2.5" />
            Points forts
          </div>
          {topStrengths.map((s, i) => (
            <motion.div
              key={`${s.manager}-${s.comp}`}
              initial={{ opacity: 0, x: -6 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 1.55 + i * 0.18 }}
              className="text-[9px] leading-snug flex items-center gap-1"
              style={{ color: T.ink }}
            >
              <span
                className="rounded-full"
                style={{ width: 4, height: 4, background: T.green }}
              />
              <span className="font-medium">{s.comp}</span>
              <span style={{ color: T.inkSoft }}>· {s.manager}</span>
              <span
                className="font-bold tabular-nums ml-auto"
                style={{ color: T.green }}
              >
                {s.score}
              </span>
            </motion.div>
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 8 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 1.4 }}
          className="rounded-lg p-1.5"
          style={{
            background: '#FFFFFF',
            border: `1px solid #E0A64133`,
            boxShadow: '0 4px 10px -8px #E0A64188',
          }}
        >
          <div
            className="text-[8px] uppercase tracking-[0.16em] font-bold mb-1 flex items-center gap-1"
            style={{ color: '#B57E15' }}
          >
            <AlertTriangle className="w-2.5 h-2.5" />
            Points d'attention
          </div>
          {topAttentions.map((a, i) => (
            <motion.div
              key={`${a.manager}-${a.comp}`}
              initial={{ opacity: 0, x: 6 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 1.55 + i * 0.18 }}
              className="text-[9px] leading-snug flex items-center gap-1"
              style={{ color: T.ink }}
            >
              <span
                className="rounded-full"
                style={{ width: 4, height: 4, background: '#E0A641' }}
              />
              <span className="font-medium">{a.comp}</span>
              <span style={{ color: T.inkSoft }}>· {a.manager}</span>
              <span
                className="font-bold tabular-nums ml-auto"
                style={{ color: '#B57E15' }}
              >
                {a.score}
              </span>
            </motion.div>
          ))}
        </motion.div>
      </div>

      {/* Lecture synthétique IA + Tendance */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.95 }}
        className="rounded-lg p-2 flex items-center justify-between gap-2 mt-auto"
        style={{
          background: 'linear-gradient(135deg, #F4EFFF 0%, #FFFFFF 100%)',
          border: `1px solid rgba(122,79,207,0.30)`,
        }}
      >
        <div className="flex items-start gap-1.5 flex-1">
          <Sparkles className="w-3 h-3 flex-shrink-0 mt-0.5" style={{ color: T.violet }} />
          <div>
            <div
              className="text-[8px] uppercase tracking-[0.14em] font-bold"
              style={{ color: T.violet }}
            >
              Lecture IA · Tendance +14 pts en 3 mois
            </div>
            <div className="text-[10px] leading-snug font-medium" style={{ color: T.ink }}>
              Le groupe progresse, mais des écarts de décision persistent sur certains managers.
            </div>
          </div>
        </div>
        <svg width="50" height="16" viewBox="0 0 60 16" className="flex-shrink-0">
          <motion.path
            d="M2 13 L12 11 L22 12 L32 8 L42 5 L52 3"
            stroke={T.violet}
            strokeWidth="1.5"
            fill="none"
            strokeLinecap="round"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 0.9, delay: 2.2 }}
          />
          <motion.circle
            cx="52"
            cy="3"
            r="1.6"
            fill={T.violet}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 3.0 }}
          />
        </svg>
      </motion.div>
    </div>
  );
}

/* ============================================================
   06 · SALLE PULSE — projection direction/RH (refonte dense)
   ============================================================ */
export function ScenePulseRoom() {
  const insights = [
    { label: 'Score moyen équipe', value: '82', delta: '+6', tone: 'up' },
    { label: 'Managers validés', value: '4/6', delta: '+2', tone: 'up' },
    { label: 'Risque rétention', value: 'Faible', delta: '-12%', tone: 'down' },
  ];
  const bullets = [
    {
      tag: 'Force',
      text: 'Décision factuelle solide sur 5 managers sur 6.',
      accent: T.green,
    },
    {
      tag: 'Vigilance',
      text: 'Posture managériale à renforcer sur la zone Sud-Est.',
      accent: '#E0A641',
    },
    {
      tag: 'Décision',
      text: 'Lancer un parcours certifiant ciblé sur la clarté du cadre.',
      accent: T.violet,
    },
  ];

  return (
    <div className="absolute inset-0 p-4 sm:p-5 flex flex-col gap-2.5">
      <div className="flex items-center justify-between">
        <span
          className="text-[9px] uppercase tracking-[0.20em] font-semibold"
          style={{ color: T.goldDeep }}
        >
          Salle Pulse · Lecture stratégique
        </span>
        <span
          className="text-[9px] font-medium px-1.5 py-0.5 rounded-full"
          style={{
            background: 'rgba(72,130,90,0.12)',
            color: T.green,
            border: '1px solid rgba(72,130,90,0.30)',
          }}
        >
          Anonymisé
        </span>
      </div>
      <div
        className="font-serif"
        style={{ color: T.ink, fontSize: '0.95rem', fontWeight: 600, lineHeight: 1.18 }}
      >
        Pilotage stratégique du leadership
      </div>

      {/* Écran de projection — perspective + ambient lighting */}
      <motion.div
        initial={{ opacity: 0, y: 12, rotateX: 8 }}
        animate={{ opacity: 1, y: 0, rotateX: 2 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="relative rounded-xl overflow-hidden"
        style={{
          background: `linear-gradient(135deg, #1C1147 0%, #3A2680 100%)`,
          border: `1px solid rgba(212,175,55,0.30)`,
          boxShadow:
            '0 18px 40px -14px rgba(28,17,71,0.65), 0 0 0 1px rgba(255,255,255,0.04) inset',
          transformStyle: 'preserve-3d',
          perspective: '900px',
        }}
      >
        <motion.div
          aria-hidden
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'radial-gradient(60% 50% at 50% 30%, rgba(243,208,96,0.20) 0%, rgba(243,208,96,0) 70%)',
          }}
          animate={{ opacity: [0.5, 0.85, 0.5] }}
          transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
        />
        <div className="px-3 pt-2 pb-1 flex items-center justify-between border-b border-white/10 relative z-10">
          <span className="text-[9px] uppercase tracking-[0.20em] font-semibold text-[#F3D060]">
            Pulse · Q1 2026
          </span>
          <Presentation className="w-3 h-3 text-[#F3D060]" />
        </div>

        {/* Sparkline + KPI compacts */}
        <div className="px-3 pt-2 pb-1 relative z-10">
          <svg width="100%" height="34" viewBox="0 0 200 34" preserveAspectRatio="none">
            <motion.path
              d="M2 28 L30 24 L60 22 L90 16 L120 13 L150 9 L188 4"
              stroke="#F3D060"
              strokeWidth="1.6"
              fill="none"
              strokeLinecap="round"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ duration: 1.4, delay: 0.6 }}
            />
            <motion.path
              d="M2 28 L30 24 L60 22 L90 16 L120 13 L150 9 L188 4 L188 34 L2 34 Z"
              fill="rgba(243,208,96,0.14)"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.6 }}
            />
            <motion.circle
              cx="188"
              cy="4"
              r="2.2"
              fill="#F3D060"
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 2.0, type: 'spring', stiffness: 320 }}
            />
          </svg>
        </div>
        <div className="px-3 pb-2 grid grid-cols-3 gap-1.5 relative z-10">
          {insights.map((kpi, i) => (
            <motion.div
              key={kpi.label}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 + i * 0.12 }}
              className="rounded-md px-1.5 py-1"
              style={{
                background: 'rgba(255,255,255,0.06)',
                border: '1px solid rgba(255,255,255,0.10)',
              }}
            >
              <div className="flex items-baseline justify-between gap-1">
                <div
                  className="font-serif text-[14px] font-bold leading-none"
                  style={{ color: '#F3D060' }}
                >
                  {kpi.value}
                </div>
                <div
                  className="text-[8px] font-bold tabular-nums"
                  style={{
                    color: kpi.tone === 'up' ? '#9FE5B5' : '#F3D060',
                  }}
                >
                  {kpi.delta}
                </div>
              </div>
              <div
                className="text-[7.5px] mt-0.5 uppercase tracking-[0.12em]"
                style={{ color: 'rgba(243,208,96,0.78)' }}
              >
                {kpi.label}
              </div>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Bullets stratégiques cascadés (présentation à la direction) */}
      <div className="flex flex-col gap-1">
        <div
          className="text-[8.5px] uppercase tracking-[0.16em] font-semibold"
          style={{ color: T.inkSoft }}
        >
          Lecture direction · Décisions clés
        </div>
        {bullets.map((b, i) => (
          <motion.div
            key={b.tag}
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 1.0 + i * 0.18, duration: 0.45 }}
            className="flex items-center gap-2 px-2 py-1 rounded-md"
            style={{
              background: '#FFFFFF',
              border: `1px solid ${T.border}`,
              boxShadow: '0 2px 6px -4px rgba(28,17,71,0.18)',
            }}
          >
            <motion.span
              className="rounded-full"
              style={{
                width: 6,
                height: 6,
                background: b.accent,
                boxShadow: `0 0 6px ${b.accent}`,
              }}
              animate={{ opacity: [0.55, 1, 0.55] }}
              transition={{ duration: 2.2, repeat: Infinity, delay: i * 0.3 }}
            />
            <span
              className="text-[8px] uppercase tracking-[0.14em] font-bold"
              style={{ color: b.accent, minWidth: 50 }}
            >
              {b.tag}
            </span>
            <span
              className="text-[10px] font-medium flex-1"
              style={{ color: T.ink, lineHeight: 1.2 }}
            >
              {b.text}
            </span>
          </motion.div>
        ))}
      </div>

      {/* Focus manager + recommandation IA — densifie le centre du tableau */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.55 }}
        className="rounded-lg p-2 grid grid-cols-[1fr_auto] gap-2 items-center"
        style={{
          background: '#FFFFFF',
          border: `1px solid ${T.border}`,
        }}
      >
        <div>
          <div
            className="text-[8px] uppercase tracking-[0.18em] font-bold mb-0.5"
            style={{ color: T.violet }}
          >
            Focus · Pôle Sud-Est
          </div>
          <div
            className="text-[10px] font-medium leading-tight"
            style={{ color: T.ink }}
          >
            Posture managériale&nbsp;:{' '}
            <span style={{ color: '#E0A641', fontWeight: 700 }}>62</span>
            <span style={{ color: T.inkSoft }}>/100</span> ·
            écart à la cible&nbsp;
            <span style={{ color: '#E0A641', fontWeight: 700 }}>−18 pts</span>
          </div>
          <div className="mt-1 flex items-center gap-1.5">
            <div
              className="h-1 rounded-full overflow-hidden flex-1"
              style={{ background: 'rgba(28,17,71,0.06)' }}
            >
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: '62%' }}
                transition={{ duration: 0.9, delay: 1.8 }}
                className="h-full rounded-full"
                style={{
                  background: 'linear-gradient(90deg, #E0A641, #F3D060)',
                }}
              />
            </div>
            <span
              className="text-[8px] font-bold tabular-nums"
              style={{ color: '#E0A641', minWidth: 22, textAlign: 'right' }}
            >
              62
            </span>
          </div>
        </div>
        <div
          className="rounded-md px-2 py-1 text-[8.5px] font-bold uppercase tracking-[0.14em] flex items-center gap-1"
          style={{
            background: 'rgba(122,79,207,0.10)',
            border: '1px solid rgba(122,79,207,0.30)',
            color: T.violet,
          }}
        >
          <Zap className="w-3 h-3" />
          Module 04
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.7 }}
        className="inline-flex items-center gap-2 self-start px-2.5 py-1 rounded-full"
        style={{
          background: 'rgba(212,175,55,0.10)',
          border: '1px solid rgba(212,175,55,0.30)',
        }}
      >
        <Eye className="w-3 h-3" style={{ color: T.goldDeep }} />
        <span
          className="text-[9px] font-semibold uppercase tracking-[0.14em]"
          style={{ color: T.goldDeep }}
        >
          Direction · RH · Aligné sur preuves
        </span>
      </motion.div>
    </div>
  );
}

/* ============================================================
   06b · CERTIFICAT — délivrance cinematic (signature + tampon)
   ============================================================ */
export function SceneCertificate() {
  const today = new Date().toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
  return (
    <div className="absolute inset-0 flex items-center justify-center overflow-hidden">
      {/* Halo doré pulsé en fond */}
      <motion.div
        aria-hidden
        initial={{ opacity: 0 }}
        animate={{ opacity: [0.55, 0.9, 0.55] }}
        transition={{ duration: 3.5, repeat: Infinity, ease: 'easeInOut' }}
        className="absolute inset-0"
        style={{
          background:
            'radial-gradient(60% 50% at 50% 50%, rgba(212,175,55,0.40) 0%, rgba(212,175,55,0) 70%)',
        }}
      />
      {/* Particules dorées discrètes */}
      {Array.from({ length: 10 }).map((_, i) => (
        <motion.span
          key={i}
          aria-hidden
          className="absolute rounded-full"
          style={{
            width: 3,
            height: 3,
            background: T.gold,
            boxShadow: `0 0 6px ${T.gold}`,
            left: `${10 + i * 8}%`,
            top: `${20 + (i % 4) * 18}%`,
          }}
          initial={{ opacity: 0, y: 0 }}
          animate={{
            opacity: [0, 0.85, 0],
            y: [-4, -22],
          }}
          transition={{
            duration: 2.4,
            delay: 0.3 + i * 0.12,
            repeat: Infinity,
            repeatDelay: 1.6,
          }}
        />
      ))}

      {/* Carte certificat — élévation + bordure dorée */}
      <motion.div
        initial={{ opacity: 0, y: 28, scale: 0.94 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        className="relative w-[88%] max-w-[460px] rounded-[18px] px-6 py-6 sm:px-7 sm:py-7"
        style={{
          background: 'linear-gradient(180deg, #FFFCF4 0%, #FFF7E2 100%)',
          border: `1.5px solid ${T.gold}`,
          boxShadow:
            '0 30px 60px -20px rgba(28,17,71,0.45), 0 0 0 1px rgba(255,255,255,0.7) inset, 0 1px 0 rgba(212,175,55,0.5) inset',
        }}
      >
        {/* En-tête */}
        <div className="text-center mb-4">
          <div
            className="text-[9px] sm:text-[10px] uppercase tracking-[0.32em] font-semibold mb-1"
            style={{ color: T.goldDeep }}
          >
            Certificat de progression
          </div>
          <div
            className="font-serif text-[16px] sm:text-[18px] font-semibold"
            style={{ color: T.ink }}
          >
            CoachDIA
          </div>
        </div>

        {/* Bénéficiaire */}
        <div className="text-center mb-3">
          <div
            className="text-[9px] uppercase tracking-[0.18em] font-semibold mb-1"
            style={{ color: T.inkSoft }}
          >
            Délivré à
          </div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="font-serif text-[20px] sm:text-[22px] font-bold"
            style={{ color: T.ink }}
          >
            Marie Dupont
          </motion.div>
        </div>

        {/* Module + critères */}
        <div
          className="rounded-xl px-4 py-3 mb-4 text-center"
          style={{
            background: 'rgba(255,255,255,0.6)',
            border: `1px solid rgba(212,175,55,0.35)`,
          }}
        >
          <div
            className="text-[9px] uppercase tracking-[0.18em] font-semibold mb-1"
            style={{ color: T.goldDeep }}
          >
            Module 03 · Posture & Fondamentaux
          </div>
          <div className="text-[11px]" style={{ color: T.ink, lineHeight: 1.35 }}>
            Module entièrement validé sur preuves
            <br />
            <span style={{ color: T.inkSoft, fontSize: '10px' }}>
              2 objectifs · 10 critères observés
            </span>
          </div>
        </div>

        {/* Signature manuscrite + tampon */}
        <div className="flex items-end justify-between gap-4">
          {/* Signature SVG path animé */}
          <div className="flex flex-col items-center flex-1">
            <svg
              width="100"
              height="38"
              viewBox="0 0 200 80"
              fill="none"
              style={{ overflow: 'visible' }}
            >
              <motion.path
                d="M10 55 C 30 25, 50 75, 70 40 S 110 25, 130 55 S 170 30, 190 50"
                stroke={T.ink}
                strokeWidth="2.4"
                strokeLinecap="round"
                fill="none"
                initial={{ pathLength: 0, opacity: 0.85 }}
                animate={{ pathLength: 1, opacity: 1 }}
                transition={{ duration: 1.4, delay: 0.7, ease: 'easeInOut' }}
              />
              <motion.path
                d="M50 60 L 70 64"
                stroke={T.ink}
                strokeWidth="1.6"
                strokeLinecap="round"
                fill="none"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 0.4, delay: 2.0, ease: 'easeInOut' }}
              />
            </svg>
            <div
              className="text-[9px] mt-1 uppercase tracking-[0.16em] font-semibold"
              style={{ color: T.inkSoft }}
            >
              Direction pédagogique
            </div>
          </div>

          {/* Sceau doré (effet tampon) */}
          <motion.div
            initial={{ scale: 0, rotate: -25, opacity: 0 }}
            animate={{ scale: [0, 1.18, 1], rotate: [-25, -8, -10], opacity: 1 }}
            transition={{
              duration: 0.55,
              delay: 1.7,
              ease: [0.22, 1.6, 0.36, 1],
              times: [0, 0.6, 1],
            }}
            className="relative"
          >
            <div
              className="w-16 h-16 sm:w-[74px] sm:h-[74px] rounded-full flex flex-col items-center justify-center text-center"
              style={{
                background:
                  'radial-gradient(ellipse at 30% 30%, rgba(255,255,255,0.55), transparent 60%), linear-gradient(135deg, #B7901F 0%, #D4AF37 50%, #F3D060 100%)',
                border: `2px solid ${T.goldDeep}`,
                boxShadow:
                  '0 4px 14px -4px rgba(183,144,31,0.65), 0 0 0 3px rgba(212,175,55,0.18)',
                color: '#3A2200',
              }}
            >
              <Sparkles className="w-3 h-3" />
              <div
                className="font-serif text-[8px] uppercase tracking-[0.18em] font-bold mt-0.5"
                style={{ color: '#3A2200' }}
              >
                Validé
              </div>
              <div
                className="font-serif text-[7px] mt-0.5 opacity-80"
                style={{ color: '#3A2200' }}
              >
                {today}
              </div>
            </div>
          </motion.div>
        </div>

        {/* Mentions légales discrètes */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2.4 }}
          className="text-center mt-4 flex items-center justify-center gap-3 text-[9px]"
          style={{ color: T.inkSoft }}
        >
          <span className="inline-flex items-center gap-1">
            <CheckCircle2 className="w-3 h-3" style={{ color: T.green }} />
            Opposable
          </span>
          <span aria-hidden>·</span>
          <span>Partageable</span>
          <span aria-hidden>·</span>
          <span>Vérifiable</span>
        </motion.div>
      </motion.div>
    </div>
  );
}
export function SceneTransformation({ onPrimaryCta, onSecondaryCta }) {
  return (
    <div className="absolute inset-0 p-5 sm:p-7 flex flex-col gap-3 items-center justify-center text-center">
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="w-14 h-14 rounded-2xl flex items-center justify-center"
        style={{
          background: `linear-gradient(135deg, ${T.goldDeep} 0%, ${T.gold} 50%, ${T.goldLight} 100%)`,
          boxShadow: `0 0 0 8px ${T.gold}22, 0 18px 36px -12px ${T.goldDeep}aa`,
        }}
      >
        <Award className="w-7 h-7 text-white" />
      </motion.div>
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.25 }}
        className="font-serif"
        style={{
          color: T.ink,
          fontSize: '1.2rem',
          fontWeight: 700,
          lineHeight: 1.15,
          maxWidth: 360,
        }}
      >
        Là où le leadership devient observable.
      </motion.div>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.45 }}
        className="text-[12px] leading-snug"
        style={{ color: T.inkSoft, maxWidth: 360 }}
      >
        Vos managers progressent sur preuves. Vos équipes mesurent leurs avancées.
      </motion.div>

      {/* CTA premium — phrase d'accroche forte (déclenchée 0.85s) */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.85, duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
        className="rounded-2xl px-4 py-3 mt-1"
        style={{
          background: `linear-gradient(135deg, ${T.ink} 0%, #2A1A6E 100%)`,
          border: `1px solid ${T.gold}66`,
          boxShadow: `0 16px 40px -16px ${T.ink}cc, 0 0 0 1px ${T.gold}22 inset`,
          maxWidth: 460,
        }}
      >
        <div
          className="text-[9px] uppercase tracking-[0.28em] font-bold mb-1"
          style={{ color: T.goldLight }}
        >
          Passez à l'action
        </div>
        <div
          className="font-serif text-[14px] sm:text-[15px] leading-snug"
          style={{ color: '#FFFFFF', fontWeight: 600 }}
        >
          Rendez votre leadership{' '}
          <span style={{ color: T.goldLight }}>mesurable</span>
          {' '}— commencez maintenant votre parcours CoachDIA.
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.15 }}
        className="flex flex-col sm:flex-row items-center gap-2 mt-2"
      >
        <motion.button
          type="button"
          onClick={onPrimaryCta}
          data-testid="demo-cta-primary"
          whileHover={{ scale: 1.04 }}
          whileTap={{ scale: 0.98 }}
          animate={{
            boxShadow: [
              `0 8px 22px -8px ${T.ink}aa, 0 0 0 0 ${T.gold}66`,
              `0 8px 22px -8px ${T.ink}aa, 0 0 0 8px ${T.gold}00`,
            ],
          }}
          transition={{
            boxShadow: { duration: 1.6, repeat: Infinity, ease: 'easeOut' },
          }}
          className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full text-[14px] font-bold transition-all"
          style={{
            background: `linear-gradient(135deg, ${T.goldDeep}, ${T.gold}, ${T.goldLight})`,
            color: T.ink,
          }}
        >
          Commencer maintenant
          <ArrowRight className="w-4 h-4" />
        </motion.button>
        <button
          type="button"
          onClick={onSecondaryCta}
          data-testid="demo-cta-secondary"
          className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full text-[13px] font-bold transition-all hover:scale-[1.02] active:scale-[0.98]"
          style={{
            background: 'linear-gradient(135deg, #FFFCEC 0%, #FFFFFF 55%, #F7F2E2 100%)',
            color: T.ink,
            border: `1.5px solid ${T.gold}`,
            boxShadow: `0 6px 18px -8px ${T.ink}33, inset 0 1px 0 rgba(255,255,255,0.6)`,
          }}
        >
          Découvrir les parcours
        </button>
      </motion.div>
      <DemoShareCluster />
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4 }}
        className="inline-flex items-center gap-1.5 mt-1"
      >
        <Sparkles className="w-3 h-3" style={{ color: T.goldDeep }} />
        <span className="text-[10px] font-medium" style={{ color: T.inkSoft }}>
          CoachDIA · Leadership mesurable
        </span>
      </motion.div>
    </div>
  );
}


/* ============================================================
   ENTRAINEMENT LIBRE — Scene 04 V2 (Iter351)
   Pont strategique theorie/certifiant : "Tu testes sans etre juge."
   ============================================================ */

export function SceneEntrainementLibre() {
  const exchanges = [
    { who: 'owen', txt: 'Pierre vient de dire qu\'il refuse la deadline. Tu fais quoi ?' },
    { who: 'user', txt: 'Je lui demande pourquoi.' },
    { who: 'owen', txt: 'Trop ouvert. Recadre.' },
    { who: 'user', txt: 'OK — qu\'est-ce qui te bloque concrètement ?' },
  ];

  return (
    <div className="absolute inset-0 p-5 sm:p-7 flex flex-col gap-2.5">
      <div className="flex items-center justify-between">
        <span className="text-[9px] uppercase tracking-[0.20em] font-semibold" style={{ color: T.violet }}>
          Entraînement libre · sans note
        </span>
        <motion.div
          initial={{ opacity: 0, scale: 0.92 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4 }}
          className="rounded-full px-2 py-0.5 flex items-center gap-1"
          style={{ background: '#FFF7E6', border: `1px solid ${T.gold}` }}
        >
          <Zap className="w-2.5 h-2.5" style={{ color: T.goldDeep }} />
          <span className="text-[8.5px] font-semibold" style={{ color: T.goldDeep }}>
            Conflit d'équipe
          </span>
        </motion.div>
      </div>

      <div className="font-serif" style={{ color: T.ink, fontSize: '1.05rem', fontWeight: 600, lineHeight: 1.2 }}>
        Tu testes. Tu te trompes. Tu recommences.
      </div>

      <motion.div
        initial={{ opacity: 0, y: -4 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="rounded-lg px-2.5 py-1.5 flex items-center gap-2"
        style={{
          background: 'linear-gradient(90deg, #F5F0FF 0%, #FFFFFF 100%)',
          border: `1px solid ${T.violet}`,
        }}
      >
        <Unlock className="w-3 h-3" style={{ color: T.violet }} />
        <span className="text-[9.5px] font-semibold" style={{ color: T.violet }}>
          Aucun verdict · aucune note
        </span>
        <span className="text-[8.5px]" style={{ color: T.inkSoft }}>
          · La compétence se construit
        </span>
      </motion.div>

      <div
        className="rounded-xl p-2.5 flex-1 flex flex-col gap-1.5"
        style={{
          background: 'linear-gradient(180deg, #FFFFFF 0%, #FAFAFB 100%)',
          border: `1px solid ${T.border}`,
        }}
      >
        {exchanges.map((ex, i) => {
          const isOwen = ex.who === 'owen';
          return (
            <motion.div
              key={i}
              initial={{ opacity: 0, x: isOwen ? -8 : 8 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 + i * 0.35, duration: 0.35 }}
              className={`flex ${isOwen ? 'justify-start' : 'justify-end'}`}
            >
              <div
                className="max-w-[85%] rounded-lg px-2.5 py-1.5"
                style={{
                  background: isOwen ? '#FFFCEC' : '#F5F0FF',
                  border: `1px solid ${isOwen ? T.gold : T.violet}`,
                }}
              >
                <div className="text-[8px] uppercase tracking-[0.16em] font-bold mb-0.5" style={{ color: isOwen ? T.goldDeep : T.violet }}>
                  {isOwen ? 'Owen' : 'Toi'}
                </div>
                <div className="text-[10px] leading-tight" style={{ color: T.ink }}>
                  {ex.txt}
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8 }}
        className="flex items-center justify-center gap-1.5 pt-0.5"
      >
        <Brain className="w-3 h-3" style={{ color: T.inkSoft }} />
        <span className="text-[9px] font-medium" style={{ color: T.inkSoft }}>
          Recommence autant que tu veux · avant l'épreuve
        </span>
      </motion.div>
    </div>
  );
}


/* ============================================================
   ENTRAINEMENT LIBRE — Scene 04 V2 (Iter351)
   Pont strategique entre theorie et certifiant : "Tu testes,
   tu te trompes, tu apprends — sans etre juge."
   ============================================================ */
